import type { ConfirmRecipeProposalInput, ConfirmRecipeProposalResponse } from '../api/contract';
import { useEffect, useRef, useState } from 'react';
import { matchIngredient, planRecipeProposal, type IngredientMatch, type Unit } from '@alexiarest/shared';
import { useConfirmRecipeProposal, useProposeRecipe, useMe, useProposeRecipePhoto } from '../api/hooks';
import { prepararImagen, ACCEPT_ESCANER } from '../imagenEscaner';
import { useDictation } from '../hooks/useDictation';
import { isDemoMode } from '../api/client';
import type { RecipeProposalResponse } from '../api/contract';
import { Drawer } from './Drawer';
import { TextField } from './Field';
import { UnitSelectField } from './UnitSelectField';
import { useToast } from './Toast';

interface EditableLine {
  isSubrecipe?: boolean;
  rawText: string;
  ingredientName: string;
  ingredientId: string;
  qty: string;
  unit: Unit;
  wastePct: string;
  match: IngredientMatch;
  estimated: boolean;
  conversionNote?: string;
}

function MicIcon({ active = false }: { active?: boolean }): JSX.Element {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="alexia-talk__mic-icon">
      <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" />
      <path d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v4M8.5 21h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      {active ? <circle cx="12" cy="9" r="10" fill="none" stroke="currentColor" strokeWidth="1" opacity=".35" /> : null}
    </svg>
  );
}

export function TellAlexiaRecipeDrawer({
  open,
  onClose,
  onSaved,
  initialProposal,
  confirmProposal,
  targetRecipeId,
  initialMode,
}: {
  targetRecipeId?: string;
  /** Pestaña con la que se abre (el alta ofrece las tres como botones). */
  initialMode?: 'text' | 'dictate' | 'photo';
  open: boolean;
  onClose: () => void;
  onSaved: (recipeId: string) => void;
  initialProposal?: RecipeProposalResponse;
  confirmProposal?: (input: ConfirmRecipeProposalInput) => Promise<ConfirmRecipeProposalResponse>;
}): JSX.Element | null {
  const propose = useProposeRecipe();
  const photo = useProposeRecipePhoto();
  const [readingPhoto, setReadingPhoto] = useState(false);
  const me = useMe();
  const dictation = useDictation(me.data?.tenant.locale ?? navigator.language ?? 'es-ES');
  const recording = dictation.listening;
  const beforeDictation = useRef('');
  const confirm = useConfirmRecipeProposal(confirmProposal);
  const toast = useToast();
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'text' | 'dictate' | 'photo'>(initialMode ?? 'text');
  useEffect(() => { if (open && initialMode) setMode(initialMode); }, [open, initialMode]);
  const [result, setResult] = useState<RecipeProposalResponse | null>(null);
  const [pendingProposals, setPendingProposals] = useState<RecipeProposalResponse[]>([]);
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [servings, setServings] = useState('1');
  const [yieldQty, setYieldQty] = useState('1');
  const [yieldUnit, setYieldUnit] = useState<Unit>('pz');
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const realAiInDemo = Boolean((import.meta.env.VITE_VOICE_API_URL ?? '').trim());
  useEffect(() => {
    if (dictation.transcript) setText([beforeDictation.current, dictation.transcript].filter(Boolean).join(' '));
  }, [dictation.transcript]);
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setRecordingSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);
  useEffect(() => {
    if (recordingSeconds >= 90 && recording) dictation.stop();
  }, [recordingSeconds, recording, dictation.stop]);
  useEffect(() => { if (!open) dictation.stop(); }, [open, dictation.stop]);

  const loadProposal = (next: RecipeProposalResponse) => {
        setResult(next);
        setName(next.proposal.name);
        setGroup(next.proposal.group ?? '');
        setServings(next.proposal.servings ? String(next.proposal.servings) : next.proposal.isSubrecipe ? '1' : '');
        setYieldQty(next.proposal.yieldQty ? String(next.proposal.yieldQty) : '');
        setYieldUnit(next.proposal.yieldUnit ?? 'pz');
        setLines(next.plan.lines.map((line) => {
          const match = line.isSubrecipe ? matchIngredient(line.ingredientName, (next.recipeCatalog ?? []).map((r) => ({ ingredientId: r.recipeId, name: r.name }))) : line.match;
          return ({
          isSubrecipe: line.isSubrecipe,
          rawText: line.rawText,
          ingredientName: line.ingredientName,
          ingredientId: line.ingredientId ?? (
            match.kind === 'exact' || match.kind === 'fuzzy'
              ? match.candidates[0].ingredientId
              : match.kind === 'none' && !line.isSubrecipe ? '__new__' : ''),
          qty: !line.issue && line.qty != null ? String(line.qty) : '',
          unit: line.issue ? 'g' : line.unit ?? 'g',
          wastePct: line.wastePct != null ? String(line.wastePct) : '0',
          match,
          estimated: Boolean(line.estimated.qty || line.estimated.unit || line.estimated.wastePct),
          conversionNote: line.conversionNote,
        }); }));
  };
  useEffect(() => { if (initialProposal && open) loadProposal(initialProposal); }, [initialProposal, open]);
  if (!open) return null;
  const receiveProposals = (next: RecipeProposalResponse) => {
    setPendingProposals((next.proposals ?? []).slice(1).map((proposal, index) => ({ ...next, draftId: next.draftIds?.[index + 1], proposal, plan: planRecipeProposal(proposal, next.catalog) })));
    loadProposal(next);
  };
  const generate = (recipeText = text) => propose.mutate(recipeText, { onSuccess: receiveProposals, onError: (error) => toast.fail(error) });
  const readPhoto = async (file: File) => {
    setReadingPhoto(true);
    try {
      const prepared = await prepararImagen(file, { maxLado: 2000, maxPdfBytes: 8_000_000, errorPdf: 'El PDF debe pesar como máximo 8 MB.', errorTamanyo: 'La foto es demasiado grande. Prueba una foto de cada página.' });
      photo.mutate(prepared, { onSuccess: receiveProposals, onError: (error) => toast.fail(error) });
    } catch (error) { toast.fail(error); }
    finally { setReadingPhoto(false); }
  };

  const stopRecording = dictation.stop;
  const startRecording = () => {
    beforeDictation.current = text.trim();
    setRecordingSeconds(0);
    dictation.start();
  };
  const closeDrawer = () => { dictation.stop(); onClose(); };

  const setLine = (index: number, patch: Partial<EditableLine>) =>
    setLines((current) => current.map((line, i) => i === index ? { ...line, ...patch } : line));

  const removeLine = (index: number) =>
    setLines((current) => current.filter((_, i) => i !== index));

  const addLine = () => setLines((current) => [...current, {
    rawText: 'Añadido manualmente',
    ingredientName: '',
    ingredientId: '__new__',
    qty: '',
    unit: 'g',
    wastePct: '0',
    match: { kind: 'none', candidates: [] },
    estimated: false,
  }]);

  const selectCatalogIngredient = (index: number, ingredientId: string) => {
    const selected = lines[index].isSubrecipe ? result?.recipeCatalog?.find((r) => r.recipeId === ingredientId) : result?.catalog?.find((ingredient) => ingredient.ingredientId === ingredientId);
    setLine(index, {
      ingredientId,
      ...(selected ? { ingredientName: selected.name } : {}),
    });
  };

  const save = () => {
    const parsedServings = Number(servings);
    const invalid = lines.find((line) => !line.ingredientName.trim() || !(Number(line.qty) > 0) || !line.unit || !line.ingredientId);
    if (!name.trim() || !Number.isInteger(parsedServings) || !(parsedServings > 0) || invalid || (result?.proposal.isSubrecipe && !(Number(yieldQty) > 0))) {
      toast.notify('Completa el nombre, las raciones y las líneas marcadas antes de guardar.', 'error');
      return;
    }
    confirm.mutate({
      draftId: result?.draftId,
      recipeId: result?.proposal.recipeId ?? targetRecipeId,
      name: name.trim(),
      group: group.trim() || undefined,
      servings: Math.round(parsedServings),
      steps: result?.proposal.steps,
      isSubrecipe: result?.proposal.isSubrecipe,
      yieldQty: result?.proposal.isSubrecipe ? Number(yieldQty) : undefined,
      yieldUnit: result?.proposal.isSubrecipe ? yieldUnit : undefined,
      lines: lines.map((line) => ({
        ingredientName: line.ingredientName.trim(),
        ingredientId: !line.isSubrecipe && line.ingredientId && line.ingredientId !== '__new__' ? line.ingredientId : undefined,
        recipeId: line.isSubrecipe ? line.ingredientId : undefined,
        createNew: line.ingredientId === '__new__',
        qty: Number(line.qty),
        unit: line.unit,
        wastePct: Number(line.wastePct) || 0,
        estimated: line.estimated || undefined,
      })),
    }, {
      onSuccess: ({ recipe, newIngredients }) => {
        toast.success(`Receta guardada${newIngredients.length ? ` con ${newIngredients.length} ingredientes pendientes de precio` : ''}.`);
        if (pendingProposals.length) {
          const [next, ...rest] = pendingProposals;
          const catalog = [...next.catalog, ...newIngredients];
          setPendingProposals(rest.map((entry) => ({ ...entry, catalog })));
          loadProposal({ ...next, catalog, plan: planRecipeProposal(next.proposal, catalog) });
        } else onSaved(recipe.recipeId);
      },
      onError: (error) => toast.fail(error),
    });
  };

  return (
    <Drawer
      open
      title="Cuéntale a Alexia"
      subtitle={result ? '¡Ya casi está! Revisa lo que he entendido antes de guardarlo.' : 'Habla con naturalidad. Yo ordeno la receta por ti.'}
      onClose={closeDrawer}
      width="52rem"
      footer={result ? (
        <>
          <button type="button" className="btn btn--ghost" onClick={() => setResult(null)}>← Volver</button>
          <button type="button" className="btn alexia-talk__save" disabled={confirm.isPending} onClick={save}>
            {confirm.isPending ? 'Guardando…' : (lines.length ? '✓ ¡Crear mi receta!' : 'Guardar plato sin escandallo')}
          </button>
        </>
      ) : undefined}
    >
      {!result ? (
        <div className="stack alexia-talk">
          <ol className="alexia-talk__steps" aria-label="Progreso">
            <li className="is-active"><span>1</span> Cuéntamela</li>
            <li><span>2</span> La revisamos</li>
            <li><span>3</span> ¡Lista!</li>
          </ol>
          <div role="tablist" aria-label="Cómo contar la receta" className="chip-filter">
            {([['text', 'Escribir'], ...(dictation.supported ? [['dictate', 'Dictar']] : []), ['photo', 'Foto']] as const).map(([value, label]) => (
              <button key={value} type="button" role="tab" aria-selected={mode === value} className="chip-filter__item" onClick={() => { dictation.stop(); setMode(value as typeof mode); }}>{label}</button>
            ))}
          </div>
          <section hidden={mode !== 'dictate'} className={`alexia-talk__hero${recording ? ' is-recording' : ''}`}>
            <div className="alexia-talk__sparkles" aria-hidden="true">✦ &nbsp; ✧ &nbsp; ✦</div>
            <h3>{recording ? 'Te escucho…' : propose.isPending ? 'Estoy ordenando tu receta…' : '¿Qué cocinamos hoy?'}</h3>
            <p>
              {recording
                ? `Habla como en la cocina · ${recordingSeconds}s de 90s`
                : propose.isPending
                  ? 'Estoy encontrando ingredientes, cantidades y raciones.'
                  : 'Di el nombre, las raciones y los ingredientes. No hace falta usar palabras perfectas.'}
            </p>
            {dictation.supported && <button
              type="button"
              className={`alexia-talk__mic${recording ? ' is-recording' : ''}`}
              onClick={recording ? stopRecording : startRecording}
              disabled={propose.isPending}
              aria-label={recording ? 'Parar grabación' : 'Empezar a grabar'}
            >
              <MicIcon active={recording} />
              <span>{recording ? 'Terminar' : 'Hablar'}</span>
            </button>}
            <p className="alexia-talk__privacy">El navegador transcribe tu voz. Revisa el texto antes de enviarlo a Alexia.</p>
            {dictation.error && <p role="alert">{dictation.error}</p>}
            {isDemoMode ? (
              <p className="alexia-talk__demo">
                {realAiInDemo ? '✓ Modo local conectado a Alexia.' : 'Demo: al enviar el texto cargaremos una propuesta de ejemplo.'}
              </p>
            ) : null}
          </section>
          <div className="field" hidden={mode !== 'photo'}>
            <label className="field__label" htmlFor="alexia-recipe-photo">Foto de una ficha o carta · PDF hasta 8 MB</label>
            <input id="alexia-recipe-photo" type="file" accept={ACCEPT_ESCANER} disabled={photo.isPending || readingPhoto || propose.isPending} onChange={(event) => { const file = event.target.files?.[0]; if (file) void readPhoto(file); event.target.value = ''; }} />
            {(photo.isPending || readingPhoto) && <p role="status">Leyendo tu ficha…</p>}
          </div>

          <div className="field" hidden={mode === 'photo'}>
            <label className="field__label" htmlFor="alexia-recipe-text">Tu receta, con tus palabras</label>
            <textarea
              id="alexia-recipe-text"
              className="input"
              rows={5}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Risotto de setas, cuatro raciones: 320 gramos de arroz carnaroli, medio litro de caldo de verduras, 200 gramos de boletus…"
            />
            <p className="field__hint">Ejemplo: “Croquetas para 12 personas: medio litro de leche, 80 gramos de harina…”</p>
          </div>
          <button type="button" hidden={mode === 'photo'} className="btn btn--lg" disabled={text.trim().length < 10 || propose.isPending || recording} onClick={() => generate()}>
            {propose.isPending ? 'Preparando la magia…' : 'Ver lo que ha entendido Alexia →'}
          </button>
        </div>
      ) : (
        <div className="stack alexia-talk">
          <ol className="alexia-talk__steps" aria-label="Progreso">
            <li className="is-done"><span>✓</span> Contada</li>
            <li className="is-active"><span>2</span> Revisamos</li>
            <li><span>3</span> ¡Lista!</li>
          </ol>
          <div className="alexia-talk__success">
            <span aria-hidden="true">🎉</span>
            <div><strong>Así he entendido tu receta</strong><p>{!servings ? 'Primero, ¿para cuántas raciones cocinas estas cantidades?' : 'Revisa lo pendiente y las estimaciones antes de guardar.'}</p></div>
          </div>
          <p>{pendingProposals.length ? `Después podrás revisar ${pendingProposals.length} recetas más, una a una.` : 'Confirma esta receta cuando esté revisada.'}</p>
          <div className="grid grid--2">
            <TextField label="Nombre" required value={name} onChange={setName} />
            <TextField label="Grupo de carta" value={group} onChange={setGroup} placeholder="Principales" />
            <TextField label="Raciones" required type="number" min={1} step={1} value={servings} onChange={setServings} />
            {result.proposal.isSubrecipe && <>
              <TextField label="Rendimiento" required type="number" min={0.01} value={yieldQty} onChange={setYieldQty} />
              <UnitSelectField label="Unidad de rendimiento" value={yieldUnit} onChange={(value) => setYieldUnit(value as Unit)} />
            </>}
          </div>
          {result.proposal.notes?.length ? (
            <div className="alert">Detalles de tu elaboración: {result.proposal.notes.join(' · ')}</div>
          ) : null}
          <div className="stack">
            {lines.map((line, index) => (
              <div className="card alexia-talk__ingredient" key={`${line.rawText}-${index}`}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="alexia-talk__number">{index + 1}</span>
                  <strong>{line.ingredientName || 'Nuevo ingrediente'}</strong>
                  {line.ingredientId === '__new__' && <span className="badge badge--atencion">Nuevo · sin precio</span>}
                  <span className={`badge ${!line.qty || !line.ingredientId ? 'badge--atencion' : line.estimated ? 'badge--marca' : ''}`}>{!line.qty || !line.ingredientId ? 'Falta concretar' : line.estimated ? 'Estimado · revisa' : 'Cantidad indicada'}</span>
                  <button type="button" className="btn btn--danger btn--sm alexia-talk__remove" onClick={() => removeLine(index)} aria-label={`Eliminar ${line.ingredientName || 'ingrediente'}`}>
                    Eliminar
                  </button>
                  <span className="muted small alexia-talk__heard">“{line.rawText}”</span>
                </div>
                {line.conversionNote ? (
                  <p className="alexia-talk__estimate">⚖️ {line.conversionNote}. Puedes cambiar cualquier valor.</p>
                ) : null}
                {line.estimated && <button type="button" className="btn btn--ghost btn--sm" onClick={() => setLine(index, { estimated: false, conversionNote: undefined })}>He medido y comprobado estos valores</button>}
                <div className="grid grid--2" style={{ marginTop: '0.75rem' }}>
                  <TextField
                    label="Ingrediente"
                    required
                    value={line.ingredientName}
                    onChange={(value) => setLine(index, { ingredientName: value, ingredientId: line.isSubrecipe ? '' : '__new__', conversionNote: undefined })}
                    placeholder="Ej. Lubina"
                    error={!line.ingredientName.trim() ? 'Escribe un ingrediente.' : undefined}
                  />
                  <div className="field">
                    <label className="field__label">Coincidencia en tu catálogo</label>
                    <select className="input" value={line.ingredientId} onChange={(event) => selectCatalogIngredient(index, event.target.value)}>
                      <option value="">Elige una coincidencia…</option>
                      {!line.isSubrecipe && <option value="__new__">＋ Crear “{line.ingredientName || 'ingrediente nuevo'}”</option>}
                      {(line.isSubrecipe ? (result.recipeCatalog ?? []).map((r) => ({ ingredientId: r.recipeId, name: r.name })) : result.catalog ?? []).map((ingredient) => (
                        <option key={ingredient.ingredientId} value={ingredient.ingredientId}>{ingredient.name}</option>
                      ))}
                    </select>
                    <span className="field__hint">
                      {line.ingredientId === '__new__' ? 'Se creará sin precio para completarlo después.' : 'Comprueba especialmente las coincidencias sugeridas.'}
                    </span>
                  </div>
                  <TextField label="Cantidad" required type="number" min={0.001} step={0.001} value={line.qty} onChange={(value) => setLine(index, { qty: value })} error={!line.qty ? 'Falta una cantidad medible.' : undefined} />
                  <UnitSelectField label="Unidad" value={line.unit} onChange={(value) => setLine(index, { unit: value as Unit })} />
                  <p className="field__hint">Cantidad limpia que entra en la receta. La merma es solo lo que descartas al limpiar, no lo que se evapora al cocinar. Si dejas 0 %, el coste asume que no hay descarte.</p>
                  <TextField label="Merma" type="number" min={0} max={99} step={0.1} suffix="%" value={line.wastePct} onChange={(value) => setLine(index, { wastePct: value })} />
                </div>
              </div>
            ))}
            <button type="button" className="btn btn--ghost alexia-talk__add" onClick={addLine}>＋ Añadir otro ingrediente</button>
          </div>
        </div>
      )}
    </Drawer>
  );
}
